<template>
  <div class="color-picker-wrapper overflow-hidden" ref="pickerWrapper">
    <div class="color-picker-preview saturation-value-area" :style="{ backgroundColor: hueBackgroundColor }"
      @mousedown.prevent="startDragSV" ref="svArea">
      <div class="saturation-overlay"></div>
      <div class="value-overlay"></div>
      <div class="picker-indicator" :style="{ top: indicatorSVPos.y + 'px', left: indicatorSVPos.x + 'px' }"></div>
    </div>

    <div class="color-controls">
      <div class="sliders flex items-center px-4">
        <!-- Hue Slider -->
        <div class="slider-container px-1" ref="hueSliderTrack" @mousedown.prevent="startDragHue">
          <div class="slider hue-slider"></div>
          <div class="picker-indicator slider-indicator" :style="{ left: indicatorHuePos.x + 'px' }"></div>
        </div>
        <!-- Alpha Slider -->
        <!-- <div class="slider-container alpha-slider-container" ref="alphaSliderTrack"
            @mousedown.prevent="startDragAlpha">
            <div class="alpha-checkerboard"></div>
            <div class="slider alpha-slider" :style="{ background: alphaSliderBackground }"></div>
            <div class="picker-indicator slider-indicator" :style="{ left: indicatorAlphaPos.x + 'px' }"></div>
          </div> -->
        <div class="color-preview-final rounded-full px-1" :style="{ backgroundColor: finalRgbaColor }">
          <div class="alpha-checkerboard preview-checkerboard"></div>
        </div>
      </div>

      <div class="color-value-display px-3">
        <div class="flex items-center justify-between w-full">
          <div class="rounded-lg w-full px-2 py-1 mx-1 bg-[#F2F3F5] flex items-center">
            <span class="mr-1 text-text-gray Roboto-14">
              G
            </span>
            <span class="Roboto-12">
              {{ GSize }}
            </span>
          </div>
          <div class="rounded-lg w-full px-2 py-1 mx-1  bg-[#F2F3F5] flex items-center">
            <span class="mr-1 text-text-gray Roboto-14">
              B
            </span>
            <span class="Roboto-12">
              {{ BSize }}
            </span>
          </div>
          <div class="rounded-lg w-full px-2 py-1 mx-1 bg-[#F2F3F5] flex items-center">
            <span class="mr-1 text-text-gray Roboto-14">
              R
            </span>
            <span class="Roboto-12">
              {{ RSize }}
            </span>
          </div>
          <!-- <input type="text" :value="finalHexColor" @input="updateColorFromHexInput" maxlength="7" class="hex-input"
            readonly /> -->
        </div>
      </div>
    </div>

    <div class="flex pb-3 px-3 gap-x-4 justify-between">
      <button class="w-full text-black bg-white border border-black rounded-full px-6 py-2 Roboto-12" @click="cancel">{{
        $t('cancel') }}</button>
      <button class="w-full text-white bg-black rounded-full px-6 py-2 Roboto-12" @click="confirm">
        {{ $t('modal-png-ok') }}
      </button>
    </div>
  </div>
</template>

<script setup>
const { t } = useI18n();
const props = defineProps({
  initialColor: {
    type: String,
    default: '#ffffff' // Default color (HEX or RGBA)
  }
});
const emit = defineEmits(['update:color', 'confirm', 'cancel']);

// --- Refs for DOM elements ---
const pickerWrapper = ref(null);
const svArea = ref(null);
const hueSliderTrack = ref(null);
const alphaSliderTrack = ref(null);

// --- Internal Color State ---
// Using HSV model + Alpha internally
const hsvAlpha = reactive({ h: 0, s: 1, v: 1, a: 1 });
const lastConfirmedHsvAlpha = reactive({ h: 0, s: 1, v: 1, a: 1 });

// --- Drag State ---
const isDraggingSV = ref(false);
const isDraggingHue = ref(false);
const isDraggingAlpha = ref(false);

// --- Positioning ---
const svAreaRect = ref({ width: 0, height: 0 });
const hueSliderRect = ref({ width: 0 });
const alphaSliderRect = ref({ width: 0 });

// --- Computed Indicator Positions ---
const indicatorSVPos = computed(() => ({
  x: hsvAlpha.s * svAreaRect.value.width,
  y: (1 - hsvAlpha.v) * svAreaRect.value.height
}));

const indicatorHuePos = computed(() => ({
  x: (hsvAlpha.h / 360) * hueSliderRect.value.width
}));

const indicatorAlphaPos = computed(() => ({
  x: hsvAlpha.a * alphaSliderRect.value.width
}));


// --- Color Conversion Utilities (Simplified) ---
function hsvToRgb(h, s, v) {
  let r, g, b;
  let i = Math.floor(h * 6);
  let f = h * 6 - i;
  let p = v * (1 - s);
  let q = v * (1 - f * s);
  let t = v * (1 - (1 - f) * s);
  switch (i % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    case 5: r = v; g = p; b = q; break;
  }
  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
}

function rgbToHsv(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  let max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, v = max;
  let d = max - min;
  s = max === 0 ? 0 : d / max;
  if (max === min) {
    h = 0; // achromatic
  } else {
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return { h: h * 360, s, v };
}

function rgbToHex(r, g, b) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
}

function hexToRgb(hex) {
  let result = /^#?([a-f\\d]{2})([a-f\\d]{2})([a-f\\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

// --- Computed Styles & Values ---
const hueBackgroundColor = computed(() => {
  const { r, g, b } = hsvToRgb(hsvAlpha.h / 360, 1, 1);
  return `rgb(${r}, ${g}, ${b})`;
});

const finalRgbColor = computed(() => {
  const { r, g, b } = hsvToRgb(hsvAlpha.h / 360, hsvAlpha.s, hsvAlpha.v);
  return `rgb(${r}, ${g}, ${b})`;
});
const RSize = ref('')
const GSize = ref('')
const BSize = ref('')
const finalRgbaColor = computed(() => {
  const { r, g, b } = hsvToRgb(hsvAlpha.h / 360, hsvAlpha.s, hsvAlpha.v);
  RSize.value = r;
  GSize.value = g;
  BSize.value = b;
  return `rgba(${r}, ${g}, ${b}, ${hsvAlpha.a})`;
});

const finalHexColor = computed(() => {
  const { r, g, b } = hsvToRgb(hsvAlpha.h / 360, hsvAlpha.s, hsvAlpha.v);
  return rgbToHex(r, g, b);
  // TBD: Add HEXA support later if needed
});

const alphaSliderBackground = computed(() => {
  return `linear-gradient(to right, rgba(0,0,0,0), ${finalRgbColor.value})`;
});

// --- Drag Handlers ---
function updateSV(event) {
  if (!svArea.value) return;
  const rect = svArea.value.getBoundingClientRect();
  let x = event.clientX - rect.left;
  let y = event.clientY - rect.top;

  x = Math.max(0, Math.min(x, svAreaRect.value.width));
  y = Math.max(0, Math.min(y, svAreaRect.value.height));

  hsvAlpha.s = x / svAreaRect.value.width;
  hsvAlpha.v = 1 - (y / svAreaRect.value.height);
}

function updateHue(event) {
  if (!hueSliderTrack.value) return;
  const rect = hueSliderTrack.value.getBoundingClientRect();
  let x = event.clientX - rect.left;
  x = Math.max(0, Math.min(x, hueSliderRect.value.width));
  hsvAlpha.h = Math.round((x / hueSliderRect.value.width) * 360);
}

function updateAlpha(event) {
  if (!alphaSliderTrack.value) return;
  const rect = alphaSliderTrack.value.getBoundingClientRect();
  let x = event.clientX - rect.left;
  x = Math.max(0, Math.min(x, alphaSliderRect.value.width));
  hsvAlpha.a = Math.round((x / alphaSliderRect.value.width) * 100) / 100; // Keep 2 decimal places
}

// --- Mouse Event Listeners ---
function handleMouseMove(event) {
  if (isDraggingSV.value) updateSV(event);
  if (isDraggingHue.value) updateHue(event);
  if (isDraggingAlpha.value) updateAlpha(event);
}

function handleMouseUp() {
  isDraggingSV.value = false;
  isDraggingHue.value = false;
  isDraggingAlpha.value = false;
}

function startDragSV(event) {
  if (!svArea.value) return;
  svAreaRect.value = { width: svArea.value.clientWidth, height: svArea.value.clientHeight };
  isDraggingSV.value = true;
  updateSV(event);
}

function startDragHue(event) {
  if (!hueSliderTrack.value) return;
  hueSliderRect.value = { width: hueSliderTrack.value.clientWidth };
  isDraggingHue.value = true;
  updateHue(event);
}

function startDragAlpha(event) {
  if (!alphaSliderTrack.value) return;
  alphaSliderRect.value = { width: alphaSliderTrack.value.clientWidth };
  isDraggingAlpha.value = true;
  updateAlpha(event);
}


// --- Input Handlers ---
function updateColorFromHexInput(event) {
  // Simplified: Currently HEX input is readonly. 
  // Full implementation would parse HEX, convert to HSV, update state.
  console.warn("HEX input is currently read-only in this implementation.");
}

function updateAlphaFromInput(event) {
  let newAlpha = parseInt(event.target.value, 10);
  if (!isNaN(newAlpha)) {
    hsvAlpha.a = Math.max(0, Math.min(100, newAlpha)) / 100;
  }
}

// --- Lifecycle Hooks ---
onMounted(() => {
  // Add global listeners
  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseup', handleMouseUp);
  // Initial setup
  parseInitialColor(props.initialColor);
  Object.assign(lastConfirmedHsvAlpha, hsvAlpha); // Store initial state as "confirmed"
  // Calculate initial dimensions for indicators
  if (svArea.value) svAreaRect.value = { width: svArea.value.clientWidth, height: svArea.value.clientHeight };
  if (hueSliderTrack.value) hueSliderRect.value = { width: hueSliderTrack.value.clientWidth };
  if (alphaSliderTrack.value) alphaSliderRect.value = { width: alphaSliderTrack.value.clientWidth };

});

onBeforeUnmount(() => {
  // Clean up global listeners
  window.removeEventListener('mousemove', handleMouseMove);
  window.removeEventListener('mouseup', handleMouseUp);
});

// --- Watchers ---
watch(hsvAlpha, () => {
  emit('update:color', finalRgbaColor.value); // Emit RGBA string
}, { deep: true });

watch(() => props.initialColor, (newColor) => {
  parseInitialColor(newColor);
  Object.assign(lastConfirmedHsvAlpha, hsvAlpha); // Update last confirmed on prop change
});

// --- Action Buttons ---
function cancel() {
  // Revert to last confirmed state
  Object.assign(hsvAlpha, lastConfirmedHsvAlpha);
  emit('cancel');
  console.log('Color selection cancelled, reverted to:', lastConfirmedHsvAlpha);
}

function confirm() {
  // Store current state as the last confirmed
  Object.assign(lastConfirmedHsvAlpha, hsvAlpha);
  emit('confirm', finalRgbaColor.value, BSize.value, RSize.value, GSize.value); // Emit final RGBA string
  console.log('Color confirmed:', finalRgbaColor.value);
}

// --- Helper to parse initial color ---
function parseInitialColor(colorString) {
  // Basic parsing (extend as needed for rgba, hsl etc.)
  if (colorString && colorString.startsWith('#')) {
    const rgb = hexToRgb(colorString);
    if (rgb) {
      const { h, s, v } = rgbToHsv(rgb.r, rgb.g, rgb.b);
      hsvAlpha.h = h;
      hsvAlpha.s = s;
      hsvAlpha.v = v;
      hsvAlpha.a = 1; // Assume full alpha for HEX
    }
  } else if (colorString && colorString.startsWith('rgba')) {
    // Basic RGBA parsing
    const match = colorString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (match) {
      const r = parseInt(match[1], 10);
      const g = parseInt(match[2], 10);
      const b = parseInt(match[3], 10);
      const a = match[4] !== undefined ? parseFloat(match[4]) : 1;
      const { h, s, v } = rgbToHsv(r, g, b);
      hsvAlpha.h = h;
      hsvAlpha.s = s;
      hsvAlpha.v = v;
      hsvAlpha.a = a;
    }
  }
  // Add more parsing logic if needed (e.g., for rgb, hsl)
}

</script>

<style scoped>
.color-picker-wrapper {
  width: 220px;
  background-color: #ffffff;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-family: sans-serif;
  border: 1px solid #eee;
  /* Prevent text selection during drag */
}

.saturation-value-area {
  width: 100%;
  height: 120px;
  margin-bottom: 5px;
  border: 1px solid #eee;
  position: relative;
  overflow: hidden;
  cursor: crosshair;
}

/* Saturation/Value Gradients */
.saturation-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to right, white, rgba(255, 255, 255, 0));
}

.value-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, black, rgba(0, 0, 0, 0));
}

.picker-indicator {
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
  transform: translate(-6px, -6px);
  /* Center the indicator */
  pointer-events: none;
  /* Allow clicks to pass through */
}

.color-controls {
  margin-bottom: 15px;
}

.sliders {
  margin-bottom: 10px;
}

.slider-container {
  width: 100%;
  height: 10px;
  cursor: pointer;
  position: relative;
}

.slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 5px;
}

.hue-slider {
  background: linear-gradient(to right, red, yellow, lime, cyan, blue, magenta, red);
}

/* Alpha Slider Specific Styles */
.alpha-slider-container {
  background-color: #eee;
  /* Fallback */
}

.alpha-checkerboard {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 5px;
  /* Match slider */
  background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAAXNSR0IArs4c6QAAABNJREFUKFNjZCASMBKpjmEQAgBfFQEdBNOM1AAAAABJRU5ErkJggg==');
  background-size: 10px 10px;
  background-repeat: repeat;
  overflow: hidden;
  /* Clip gradient */
}

.alpha-slider {
  /* Background set dynamically via :style */
  background: transparent;
}


.slider-indicator {
  top: 50%;
  /* Center vertically */
  transform: translate(-6px, -50%);
  /* Center indicator on the line */
  background-color: white;
  /* Make slider handle visible */
}

.color-value-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  gap: 10px;
}

.color-preview-final {
  width: 26px;
  height: 23px;
  border: 1px solid #ccc;
  position: relative;
  overflow: hidden;
  margin-left: 10px;
  /* Needed for checkerboard */
}

.preview-checkerboard {
  width: 100%;
  height: 100%;
  border-radius: 0;
  /* Reset radius */
}

.hex-label {
  font-size: 12px;
  color: #555;
  margin-right: 5px;
}

.hex-input {
  border: none;
  outline: none;
  font-size: 13px;
  width: 65px;
  background-color: transparent;
  font-family: monospace;
  text-transform: uppercase;
  margin-right: 5px;
}

.alpha-input {
  border: none;
  outline: none;
  font-size: 13px;
  width: 35px;
  background-color: transparent;
  text-align: right;
  margin-left: auto;
  /* Push to the right */
  margin-right: 2px;
  padding: 0;
  -moz-appearance: textfield;
  /* Firefox */
}

.alpha-input::-webkit-outer-spin-button,
.alpha-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  /* Chrome, Safari, Edge, Opera */
  margin: 0;
}

.color-picker-actions {
  display: flex;
  justify-content: space-between;
  margin: 5px 0 10px 0;
}
</style>